import { zipStore, type ZipEntry } from './zip-store';

/**
 * A job's exported documents as one download: each document's package (`pages.pdf`, the searchable
 * PDF, and `document.json`, what Cortexa read) from `GET /v1/documents/{id}/package/{file}`, under
 * the name the job's file naming gave it, in one zip. A workbench document is a ledger document
 * under the engine's own id (EngineDocumentMirror), so the batch's ids are the API's. A package is
 * written when the document is exported; until then the API answers 404 and this waits for it, briefly:
 * the tour offers the download only once `packagesReady` has seen every one.
 */
export interface DocumentDownloadOptions {
  /** The API's root (`/v1`). */
  apiOrigin: string;
  tenant?: string;
  /** How long to wait for packages still being written; tests pass 0. */
  waitMs?: number;
}

const FILES = ['pages.pdf', 'document.json'] as const;
const LOOK_EVERY_MS = 2_000;

/** Every package file of the documents, waiting for those not packaged yet; throws past the wait. */
export async function packageEntries(
  documentIds: readonly string[],
  { apiOrigin, tenant, waitMs = 15_000 }: DocumentDownloadOptions,
): Promise<ZipEntry[]> {
  const until = Date.now() + waitMs;
  const entries: ZipEntry[] = [];
  const taken = new Set<string>();
  for (const id of documentIds) {
    for (const file of FILES) {
      const response = await packageFile(apiOrigin, tenant, id, file, until);
      const name = uniqueName(downloadName(response, `${id}-${file}`), taken);
      entries.push({ name, data: new Uint8Array(await response.arrayBuffer()) });
    }
  }
  return entries;
}

async function packageFile(
  apiOrigin: string,
  tenant: string | undefined,
  id: string,
  file: string,
  until: number,
): Promise<Response> {
  for (;;) {
    const response = await fetch(
      `${apiOrigin}/documents/${encodeURIComponent(id)}/package/${file}`,
      {
        credentials: 'include',
        headers: tenant ? { 'Paige-Tenant': tenant } : {},
      },
    );
    if (response.ok) return response;
    if (response.status !== 404 || Date.now() >= until)
      throw new Error(
        'Paige could not get these documents ready to download. Try again in a minute.',
      );
    await new Promise((done) => setTimeout(done, LOOK_EVERY_MS));
  }
}

/** How many of the documents have their package now: exported, and there to download. One look, no waiting. */
export async function packagesReady(
  documentIds: readonly string[],
  { apiOrigin, tenant }: DocumentDownloadOptions,
): Promise<number> {
  const found = await Promise.all(
    documentIds.map(async (id) => {
      const response = await fetch(
        `${apiOrigin}/documents/${encodeURIComponent(id)}/package/document.json`,
        { credentials: 'include', headers: tenant ? { 'Paige-Tenant': tenant } : {} },
      );
      return response.ok;
    }),
  );
  return found.filter(Boolean).length;
}

/** The file name the API gave (`Content-Disposition`), else the fallback. */
export function downloadName(response: Pick<Response, 'headers'>, fallback: string): string {
  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? fallback;
}

/**
 * Two documents filed under the same name keep both: "Invoice.pdf", "Invoice-2.pdf", the way every destination
 * files a name already taken (the Export page says so: "A name already taken gets -2, -3, ..."). Until the first-run
 * walk of 2026-10-12 the zip said "Invoice (2).pdf", so the download and the destinations named the same document
 * two ways.
 */
export function uniqueName(name: string, taken: Set<string>): string {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const extension = dot > 0 ? name.slice(dot) : '';
  let candidate = name;
  for (let n = 2; taken.has(candidate.toLowerCase()); n++) candidate = `${stem}-${n}${extension}`;
  taken.add(candidate.toLowerCase());
  return candidate;
}

/** The zip, saved the way the browser saves any download. */
export async function downloadDocuments(
  documentIds: readonly string[],
  zipName: string,
  options: DocumentDownloadOptions,
): Promise<void> {
  const blob = zipStore(await packageEntries(documentIds, options));
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
